<template>
  <div class="activity-feed" :class="{ 'no-header': !showHeader }">
    <div v-if="showHeader" class="activity-header">
      <h3 class="activity-title">
        <i class="fa fa-bolt"></i>
        Intelligent Insights
      </h3>
      <div class="header-actions">
        <div class="variant-toggle" v-if="showVariantToggle">
          <button
            v-for="v in variants"
            :key="v.value"
            class="variant-btn"
            :class="{ active: currentVariant === v.value }"
            @click="currentVariant = v.value"
            :title="v.label"
          >
            <i :class="v.icon"></i>
          </button>
        </div>
        <span class="activity-badge" v-if="allActivities.length > 0">{{ allActivities.length }}</span>
      </div>
    </div>

    <!-- Cards Variant -->
    <div v-if="currentVariant === 'cards' && displayedActivities.length > 0" class="activity-list variant-cards">
      <div
        v-for="activity in displayedActivities"
        :key="activity.id"
        class="activity-card"
        :class="[`priority-${activity.priority}`]"
        @click="handleClick(activity)"
      >
        <div class="activity-icon" :class="activity.type">
          <i :class="activity.icon"></i>
        </div>
        <div class="activity-content">
          <div class="activity-main">
            <span class="activity-title-text">{{ activity.title }}</span>
            <span class="activity-time">{{ activity.timeDisplay }}</span>
          </div>
          <p class="activity-subtitle" v-if="activity.subtitle">{{ activity.subtitle }}</p>
        </div>
        <div class="activity-actions" v-if="activity.actionable && activity.actions.length > 0">
          <button
            v-for="action in activity.actions.slice(0, 2)"
            :key="action.key"
            class="action-btn"
            :class="action.type"
            @click.stop="handleAction(activity, action)"
            :title="action.label"
          >
            <i :class="action.icon"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Timeline Variant -->
    <div v-else-if="currentVariant === 'timeline' && displayedActivities.length > 0" class="activity-list variant-timeline">
      <div
        v-for="(activity, index) in displayedActivities"
        :key="activity.id"
        class="timeline-item"
        :class="[`priority-${activity.priority}`, { 'is-last': index === displayedActivities.length - 1 }]"
        @click="handleClick(activity)"
      >
        <div class="timeline-marker" :class="activity.type">
          <i :class="activity.icon"></i>
        </div>
        <div class="timeline-content">
          <div class="timeline-header">
            <span class="timeline-title">{{ activity.title }}</span>
            <span class="timeline-time">{{ activity.timeDisplay }}</span>
          </div>
          <p class="timeline-subtitle" v-if="activity.subtitle">{{ activity.subtitle }}</p>
        </div>
      </div>
    </div>

    <!-- Bold Variant -->
    <div v-else-if="currentVariant === 'bold' && displayedActivities.length > 0" class="activity-list variant-bold">
      <div
        v-for="activity in displayedActivities"
        :key="activity.id"
        class="bold-item"
        :class="[`priority-${activity.priority}`]"
        @click="handleClick(activity)"
      >
        <div class="bold-icon" :class="activity.type">
          <i :class="activity.icon"></i>
        </div>
        <div class="bold-content">
          <span class="bold-title">{{ activity.title }}</span>
          <span class="bold-meta">
            <span class="bold-subtitle" v-if="activity.subtitle">{{ activity.subtitle }}</span>
            <span class="bold-time">{{ activity.timeDisplay }}</span>
          </span>
        </div>
        <div class="bold-priority-badge" :class="activity.priority">
          {{ activity.priorityLabel }}
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="displayedActivities.length === 0" class="activity-empty">
      <i class="fa fa-check-circle-o"></i>
      <p class="empty-title">All caught up!</p>
      <p class="empty-text">No recent activity to show</p>
    </div>

    <div v-if="allActivities.length > maxItems" class="activity-footer">
      <button class="view-all-btn" @click="$emit('view-all')">
        View All Activity
        <i class="fa fa-arrow-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import mixin from '../../../mixin.js';
import moment from 'moment-timezone';

export default {
  name: 'ActivityFeed',

  mixins: [mixin],

  props: {
    variant: {
      type: String,
      default: 'cards',
      validator: v => ['cards', 'timeline', 'bold'].includes(v)
    },
    maxItems: {
      type: Number,
      default: 8
    },
    showVariantToggle: {
      type: Boolean,
      default: true
    },
    showHeader: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      currentVariant: this.variant,
      variants: [
        { value: 'cards', label: 'Cards', icon: 'fa fa-th-large' },
        { value: 'timeline', label: 'Timeline', icon: 'fa fa-list' },
        { value: 'bold', label: 'Bold', icon: 'fa fa-bolt' }
      ]
    };
  },

  computed: {
    ...mapState({
      assignedToMeTasks: state => state.tasks?.assignedToMeTasks || [],
      myCreatedTasks: state => state.tasks?.myCreatedTasks || [],
      todaysAppointments: state => state.calendar?.todaysAppointments || [],
      conversations: state => state.conversations?.conversations || [],
      formSubmissions: state => state.forms?.formSubmissions || [],
      recentPatients: state => state.patient?.recentPatients || []
    }),

    ...mapGetters({
      todaysTasksForEmployee: 'tasks/todaysTasksForEmployee'
    }),

    allActivities() {
      const activities = [
        ...this.buildTaskActivities(),
        ...this.buildAppointmentActivities(),
        ...this.buildMessageActivities(),
        ...this.buildFormActivities(),
        ...this.buildPatientActivities()
      ];

      // Sort by priority (high first) then by timestamp (recent first)
      return activities.sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return b.timestamp - a.timestamp;
      });
    },

    displayedActivities() {
      return this.allActivities.slice(0, this.maxItems);
    }
  },

  watch: {
    variant(newVal) {
      this.currentVariant = newVal;
    }
  },

  methods: {
    buildTaskActivities() {
      const activities = [];
      const now = moment();
      const tasks = this.todaysTasksForEmployee ? this.todaysTasksForEmployee(this.userID) : [];

      tasks.forEach(task => {
        if (task.status === 'COMPLETE' || task.completed) return;

        const dueDate = task.dueAtStr || task.dueDate;
        const isOverdue = dueDate && moment(dueDate).isBefore(now, 'day');
        const isDueToday = dueDate && moment(dueDate).isSame(now, 'day');

        if (isOverdue || isDueToday) {
          activities.push({
            id: `task-${task.id}`,
            type: 'task',
            priority: isOverdue ? 'high' : 'medium',
            priorityLabel: isOverdue ? 'Overdue' : 'Due Today',
            icon: 'fa fa-check-square-o',
            title: this.getTaskContent(task),
            subtitle: task.patientName || '',
            timestamp: dueDate ? moment(dueDate).valueOf() : now.valueOf(),
            timeDisplay: isOverdue ? moment(dueDate).fromNow() : 'Due today',
            actionable: true,
            actions: [
              { key: 'view', label: 'View Task', icon: 'fa fa-eye', type: 'default' },
              { key: 'complete', label: 'Mark Complete', icon: 'fa fa-check', type: 'success' }
            ],
            route: { name: 'ToDoID', params: { taskID: task.id } },
            data: task
          });
        }
      });

      return activities;
    },

    buildAppointmentActivities() {
      const activities = [];
      const now = moment();
      const appointments = this.todaysAppointments || [];

      appointments.forEach(apt => {
        const aptTime = moment(apt.startTime || apt.appointmentTime);
        const minutesUntil = aptTime.diff(now, 'minutes');
        const isUpcoming = minutesUntil > 0 && minutesUntil <= 60;
        const isNoShow = apt.status === 'NO_SHOW' || apt.status === 'MISSED';
        const isConfirmed = apt.status === 'CONFIRMED';

        if (isNoShow) {
          activities.push({
            id: `apt-noshow-${apt.id}`,
            type: 'appointment',
            priority: 'high',
            priorityLabel: 'No Show',
            icon: 'fa fa-user-times',
            title: apt.patientName || 'Unknown Patient',
            subtitle: `Missed ${aptTime.format('h:mm A')} appointment`,
            timestamp: aptTime.valueOf(),
            timeDisplay: aptTime.fromNow(),
            actionable: true,
            actions: [
              { key: 'reschedule', label: 'Reschedule', icon: 'fa fa-calendar', type: 'default' },
              { key: 'call', label: 'Call Patient', icon: 'fa fa-phone', type: 'primary' }
            ],
            route: { name: 'Calendar', query: { appointmentId: apt.id } },
            data: apt
          });
        } else if (isUpcoming) {
          activities.push({
            id: `apt-upcoming-${apt.id}`,
            type: 'appointment',
            priority: 'medium',
            priorityLabel: 'Upcoming',
            icon: 'fa fa-calendar-check-o',
            title: apt.patientName || 'Unknown Patient',
            subtitle: apt.procedureName || apt.appointmentType || 'Appointment',
            timestamp: aptTime.valueOf(),
            timeDisplay: `In ${minutesUntil} min`,
            actionable: true,
            actions: [
              { key: 'view', label: 'View Details', icon: 'fa fa-eye', type: 'default' }
            ],
            route: { name: 'Calendar', query: { appointmentId: apt.id } },
            data: apt
          });
        } else if (isConfirmed) {
          activities.push({
            id: `apt-confirmed-${apt.id}`,
            type: 'appointment',
            priority: 'low',
            priorityLabel: 'Confirmed',
            icon: 'fa fa-calendar-check-o',
            title: apt.patientName || 'Unknown Patient',
            subtitle: `${aptTime.format('h:mm A')} - ${apt.procedureName || 'Appointment'}`,
            timestamp: aptTime.valueOf(),
            timeDisplay: aptTime.format('h:mm A'),
            actionable: false,
            actions: [],
            route: { name: 'Calendar', query: { appointmentId: apt.id } },
            data: apt
          });
        }
      });

      return activities;
    },

    buildMessageActivities() {
      const activities = [];
      const now = moment();
      const conversations = this.conversations || [];

      conversations.forEach(conv => {
        if (!conv.unreadCount || conv.unreadCount === 0) return;

        const lastMessageTime = moment(conv.lastMessageTime || conv.updatedAt);
        const minutesAgo = now.diff(lastMessageTime, 'minutes');
        const isUrgent = minutesAgo > 30;

        activities.push({
          id: `msg-${conv.id}`,
          type: 'message',
          priority: isUrgent ? 'high' : 'medium',
          priorityLabel: isUrgent ? 'Urgent' : 'New',
          icon: 'fa fa-comment',
          title: conv.patientName || conv.contactName || 'Unknown',
          subtitle: `${conv.unreadCount} unread message${conv.unreadCount > 1 ? 's' : ''}`,
          timestamp: lastMessageTime.valueOf(),
          timeDisplay: lastMessageTime.fromNow(),
          actionable: true,
          actions: [
            { key: 'reply', label: 'Reply', icon: 'fa fa-reply', type: 'primary' }
          ],
          route: { name: 'Inbox', query: { conversationId: conv.id } },
          data: conv
        });
      });

      return activities;
    },

    buildFormActivities() {
      const activities = [];
      const submissions = this.formSubmissions || [];

      submissions.slice(0, 5).forEach(submission => {
        const submittedAt = moment(submission.submittedAt || submission.createdAt);

        activities.push({
          id: `form-${submission.id}`,
          type: 'form',
          priority: 'medium',
          priorityLabel: 'New',
          icon: 'fa fa-file-text-o',
          title: submission.formName || 'Form Submission',
          subtitle: submission.patientName || submission.submitterName || 'New submission',
          timestamp: submittedAt.valueOf(),
          timeDisplay: submittedAt.fromNow(),
          actionable: true,
          actions: [
            { key: 'review', label: 'Review', icon: 'fa fa-eye', type: 'default' }
          ],
          route: { name: 'Forms', query: { submissionId: submission.id } },
          data: submission
        });
      });

      return activities;
    },

    buildPatientActivities() {
      const activities = [];
      const patients = this.recentPatients || [];

      patients.slice(0, 3).forEach(patient => {
        const createdAt = moment(patient.createdAt || patient.registeredAt);
        const isRecent = moment().diff(createdAt, 'hours') < 24;

        if (isRecent) {
          activities.push({
            id: `patient-${patient.id}`,
            type: 'patient',
            priority: 'low',
            priorityLabel: 'New',
            icon: 'fa fa-user-plus',
            title: patient.name || `${patient.firstName} ${patient.lastName}`,
            subtitle: 'New patient registered',
            timestamp: createdAt.valueOf(),
            timeDisplay: createdAt.fromNow(),
            actionable: true,
            actions: [
              { key: 'view', label: 'View Profile', icon: 'fa fa-user', type: 'default' }
            ],
            route: { name: 'PatientProfile', params: { id: patient.id } },
            data: patient
          });
        }
      });

      return activities;
    },

    getTaskContent(task) {
      let content = task.taskContent || task.content || '';
      if (typeof content === 'string') {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        content = tempDiv.textContent || tempDiv.innerText || '';
        content = content.replace(/\s+/g, ' ').trim();
      }
      return content || 'Untitled Task';
    },

    handleClick(activity) {
      if (activity.route) {
        this.$router.push(activity.route);
      }
    },

    handleAction(activity, action) {
      this.$emit('action', { activity, action });

      // Default action handling
      switch (action.key) {
        case 'view':
        case 'review':
          if (activity.route) {
            this.$router.push(activity.route);
          }
          break;
        case 'complete':
          if (activity.type === 'task' && activity.data) {
            this.$store.dispatch('tasks/updateTaskStatus', {
              taskId: activity.data.id,
              status: 'COMPLETE'
            }).catch(() => {});
          }
          break;
        case 'reply':
          if (activity.route) {
            this.$router.push(activity.route);
          }
          break;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.activity-feed {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.activity-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid #f1f5f9;
  background-color: #fafbfc;
}

.activity-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  i {
    color: #f59e0b;
    font-size: 1.125rem;
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

// Variant Toggle
.variant-toggle {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  border-radius: 6px;
  padding: 3px;
}

.variant-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    color: #334155;
  }

  &.active {
    background: white;
    color: #3abf94;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  }

  i {
    font-size: 0.75rem;
  }
}

.activity-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background-color: #3abf94;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 12px;
}

// Common Activity List Styles
.activity-list {
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
}

// ==========================================
// VARIANT: Cards
// ==========================================
.variant-cards {
  padding: 8px 0;

  .activity-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    border-bottom: 1px solid #f1f5f9;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: #f8fafc;

      .activity-actions {
        opacity: 1;
      }
    }

    &.priority-high {
      .activity-icon {
        background-color: rgba(239, 68, 68, 0.1);
        color: #ef4444;
      }
    }

    &.priority-medium {
      .activity-icon {
        background-color: rgba(245, 158, 11, 0.1);
        color: #f59e0b;
      }
    }

    &.priority-low {
      .activity-icon {
        background-color: rgba(34, 197, 94, 0.1);
        color: #22c55e;
      }
    }
  }

  .activity-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 1rem;
    }
  }

  .activity-content {
    flex: 1;
    min-width: 0;
  }

  .activity-main {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .activity-title-text {
    font-size: 0.875rem;
    font-weight: 500;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .activity-time {
    font-size: 0.75rem;
    color: #94a3b8;
    white-space: nowrap;
  }

  .activity-subtitle {
    font-size: 0.8125rem;
    color: #64748b;
    margin: 2px 0 0 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .activity-actions {
    display: flex;
    gap: 6px;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  .action-btn {
    width: 30px;
    height: 30px;
    border: 1px solid #e2e8f0;
    background: white;
    border-radius: 6px;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;

    &:hover {
      border-color: #cbd5e1;
      color: #334155;
    }

    &.success {
      &:hover {
        background-color: #22c55e;
        border-color: #22c55e;
        color: white;
      }
    }

    &.primary {
      &:hover {
        background-color: #3abf94;
        border-color: #3abf94;
        color: white;
      }
    }

    i {
      font-size: 0.75rem;
    }
  }
}

// ==========================================
// VARIANT: Timeline
// ==========================================
.variant-timeline {
  padding: 16px 16px 16px 24px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 30px;
    top: 24px;
    bottom: 24px;
    width: 2px;
    background: #e2e8f0;
  }

  .timeline-item {
    display: flex;
    gap: 16px;
    padding: 8px 0;
    cursor: pointer;
    position: relative;

    &:hover {
      .timeline-content {
        background-color: #f8fafc;
      }
    }

    &.priority-high .timeline-marker {
      background-color: #ef4444;
      border-color: #ef4444;
    }

    &.priority-medium .timeline-marker {
      background-color: #f59e0b;
      border-color: #f59e0b;
    }

    &.priority-low .timeline-marker {
      background-color: #22c55e;
      border-color: #22c55e;
    }
  }

  .timeline-marker {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: white;
    border: 2px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    z-index: 1;

    i {
      font-size: 0.6875rem;
      color: white;
    }
  }

  .timeline-content {
    flex: 1;
    min-width: 0;
    padding: 6px 12px;
    border-radius: 8px;
    transition: background-color 0.15s ease;
  }

  .timeline-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .timeline-title {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .timeline-time {
    font-size: 0.6875rem;
    color: #94a3b8;
    white-space: nowrap;
  }

  .timeline-subtitle {
    font-size: 0.75rem;
    color: #64748b;
    margin: 2px 0 0 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

// ==========================================
// VARIANT: Bold
// ==========================================
.variant-bold {
  padding: 8px 0;

  .bold-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    border-left: 4px solid transparent;

    &:hover {
      background-color: #f8fafc;
    }

    &.priority-high {
      border-left-color: #ef4444;

      .bold-icon {
        background-color: #ef4444;
      }

      .bold-priority-badge {
        background-color: rgba(239, 68, 68, 0.1);
        color: #ef4444;
      }
    }

    &.priority-medium {
      border-left-color: #f59e0b;

      .bold-icon {
        background-color: #f59e0b;
      }

      .bold-priority-badge {
        background-color: rgba(245, 158, 11, 0.1);
        color: #f59e0b;
      }
    }

    &.priority-low {
      border-left-color: #22c55e;

      .bold-icon {
        background-color: #22c55e;
      }

      .bold-priority-badge {
        background-color: rgba(34, 197, 94, 0.1);
        color: #22c55e;
      }
    }
  }

  .bold-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 1.125rem;
      color: white;
    }
  }

  .bold-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .bold-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .bold-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.8125rem;
    color: #64748b;
  }

  .bold-subtitle {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .bold-time {
    white-space: nowrap;
    color: #94a3b8;

    &::before {
      content: '•';
      margin-right: 8px;
    }
  }

  .bold-priority-badge {
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }
}

// Empty State
.activity-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #94a3b8;

  i {
    font-size: 2.5rem;
    margin-bottom: 16px;
    color: #22c55e;
    opacity: 0.6;
  }

  .empty-title {
    font-size: 1rem;
    font-weight: 600;
    color: #64748b;
    margin: 0 0 4px 0;
  }

  .empty-text {
    font-size: 0.875rem;
    color: #94a3b8;
    margin: 0;
  }
}

.activity-footer {
  padding: 14px 20px;
  border-top: 1px solid #f1f5f9;
  background-color: #fafbfc;
}

.view-all-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  background-color: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #3abf94;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(58, 191, 148, 0.05);
    border-color: #3abf94;
  }

  i {
    font-size: 0.75rem;
  }
}
</style>
